import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp, Users, FileClock, Send, EuroIcon, Home as HomeIcon, RefreshCw } from 'lucide-react';
import { colors } from '../styles/colors';
import { getTemps, calculateStats, calculateCaParMois, calculateCaParConsultant, getFacturesExternes, getFacturesInternes, getConsultants } from '../services/airtable';
import { useYear } from '../contexts/YearContext';
import PageHeader from '../components/PageHeader';
import FilterBar from '../components/FilterBar';
import KPICard from '../components/home/KPICard';
import ChartCard from '../components/home/ChartCard';
import JoursParConsultantTable from '../components/home/JoursParConsultantTable';
import ActionButton from '../components/ActionButton';
import AnimatedContent from '../components/AnimatedContent';
import { formatEuro, formatMois, CustomTooltip } from '../utils/chartFormatters';
import { getAvailableYearsFromTemps } from '../utils/yearUtils';

function Home() {
  // États Dashboard
  const [stats, setStats] = useState(null);
  const [caParMois, setCaParMois] = useState([]);
  const [caParConsultant, setCaParConsultant] = useState([]);
  const [joursParConsultant, setJoursParConsultant] = useState({ data: [], consultants: [] });
  const [overdueInvoices, setOverdueInvoices] = useState(0);
  const [pendingInvoices, setPendingInvoices] = useState(0);
  const [facturesInternesNonPayees, setFacturesInternesNonPayees] = useState([]);

  const [allTemps, setAllTemps] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [facturesInternesData, setFacturesInternesData] = useState([]);
  const { selectedYear, setSelectedYear, availableYears, setAvailableYears } = useYear();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // État refresh
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Map consultants par ID
  const consultantsMap = useMemo(() => {
    const map = {};
    consultants.forEach(c => {
      map[c.id] = {
        nom: c.fields?.Nom || '-'
      };
    });
    return map;
  }, [consultants]);

  const loadDashboardData = useCallback(async (isRefresh = false) => {
    try {
      // Chargement initial uniquement si ce n'est pas un refresh
      if (!isRefresh) {
        setLoading(true);
      }

      const [tempsData, facturesExternesData, facturesInternesRaw, consultantsData] = await Promise.all([
        getTemps(),
        getFacturesExternes(),
        getFacturesInternes(),
        getConsultants()
      ]);
      setAllTemps(tempsData);
      setConsultants(Array.isArray(consultantsData) ? consultantsData : []);
      setFacturesInternesData(Array.isArray(facturesInternesRaw) ? facturesInternesRaw : []);

      const years = getAvailableYearsFromTemps(tempsData);

      // Retourner les années pour mise à jour dans useEffect
      return { years, facturesExternesData };

    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => {
      setError(null);
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
  }, []);

  // Effet pour charger les données au montage et lors du refresh
  useEffect(() => {
    const load = async () => {
      const result = await loadDashboardData(refreshKey > 0);
      if (result) {
        const { years, facturesExternesData } = result;

        // Mettre à jour les années disponibles dans le contexte global
        setAvailableYears(years);

        // Calcul des factures en retard
        const today = new Date();
        const sixtyDaysInMs = 60 * 24 * 60 * 60 * 1000;

        const overdueCount = (facturesExternesData || []).filter(invoice => {
          const fields = invoice.fields;
          const status = fields?.Statut;
          const emissionDateStr = fields?.['Date émission'];

          if (status !== 'Payée' && emissionDateStr) {
            const emissionDate = new Date(emissionDateStr);
            return (today.getTime() - emissionDate.getTime()) > sixtyDaysInMs;
          }
          return false;
        }).length;
        setOverdueInvoices(overdueCount);

        // Calcul des factures externes en attente d'émission pour des périodes passées
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;

        const pendingCount = (facturesExternesData || []).filter(invoice => {
          const fields = invoice.fields;
          const status = fields?.Statut;
          const annee = fields?.Année;

          if (!annee || (status !== 'Brouillon' && status !== 'A envoyer')) {
            return false;
          }

          const mois = fields?.Mois;
          return mois ? (annee < currentYear || (annee === currentYear && mois < currentMonth)) : false;
        }).length;

        setPendingInvoices(pendingCount);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Calculer les factures internes non payées par consultant
  useEffect(() => {
    if (!facturesInternesData.length || !consultants.length) return;

    const facturesNonPayees = {};
    facturesInternesData.forEach(facture => {
      const fields = facture.fields;
      const statut = fields?.Statut;
      const consultantIds = fields?.Consultant || [];
      const montantTTC = fields?.['Montant total (TTC)'] || 0;

      // Ne considérer que les factures émises mais non payées
      if (statut === 'Emise' && consultantIds.length > 0) {
        const consultantId = consultantIds[0];
        const consultantNom = consultantsMap[consultantId]?.nom || 'Inconnu';

        if (!facturesNonPayees[consultantId]) {
          facturesNonPayees[consultantId] = {
            nom: consultantNom,
            montant: 0
          };
        }
        facturesNonPayees[consultantId].montant += montantTTC;
      }
    });

    // Convertir en tableau pour le graphique
    const facturesNonPayeesArray = Object.values(facturesNonPayees)
      .filter(item => item.montant > 0)
      .sort((a, b) => b.montant - a.montant);

    setFacturesInternesNonPayees(facturesNonPayeesArray);
  }, [facturesInternesData, consultants, consultantsMap]);

  useEffect(() => {
    if (!allTemps.length) return;

    // Filtrer les temps pour l'année sélectionnée
    const tempsFiltres = allTemps.filter(t => t.fields.Année === selectedYear);

    // Recalculer les stats et les données des graphiques
    setStats(calculateStats(tempsFiltres));
    setCaParMois(calculateCaParMois(tempsFiltres));
    setCaParConsultant(calculateCaParConsultant(tempsFiltres));

    // Calculer les jours par mois et par consultant
    const calculateJoursParConsultant = (temps) => {
      if (!temps || temps.length === 0) {
        return { data: [], consultants: [] };
      }

      const dataByMonth = {};
      const consultantSet = new Set();

      temps.forEach(record => {
        const {
          'Mois': mois,
          'Nombre de jours': jours
        } = record.fields;

        const consultant = record.consultantNom;

        if (mois && consultant && jours && consultant !== '-') {
          consultantSet.add(consultant);
          if (!dataByMonth[mois]) {
            dataByMonth[mois] = { mois };
          }
          dataByMonth[mois][consultant] = (dataByMonth[mois][consultant] || 0) + jours;
        }
      });

      const consultants = Array.from(consultantSet).sort();
      const data = Object.values(dataByMonth).sort((a, b) => a.mois - b.mois);

      return { data, consultants };
    };

    setJoursParConsultant(calculateJoursParConsultant(tempsFiltres));

  }, [selectedYear, allTemps]);

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      {/* PageHeader avec ActionButton */}
      <PageHeader
        icon={HomeIcon}
        title="Accueil"
        subtitle="Vue d'ensemble de votre activité"
        actions={
          <ActionButton
            icon={RefreshCw}
            text="Actualiser"
            onClick={handleRefresh}
            loading={refreshing}
          />
        }
      />

      {/* Filtre par année */}
      <FilterBar
        filters={[
          {
            type: 'select',
            value: selectedYear,
            onChange: setSelectedYear,
            options: availableYears.map(y => ({ value: y, label: y.toString() })),
            allowNull: false,
            minWidth: '120px'
          }
        ]}
      />

      {/* Gestion des états de chargement et erreur */}
      {error && (
        <div style={{
          backgroundColor: colors.background.lightRed,
          border: `1px solid ${colors.status.error}`,
          color: colors.text.darkRed,
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          Erreur : {error}
        </div>
      )}

      {(loading || !stats) ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 300px)' }}>
          <div style={{ width: '48px', height: '48px', border: `4px solid ${colors.secondary.lightTeal}`, borderTop: `4px solid ${colors.primary.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      ) : (
        <AnimatedContent>
      {/* KPI Cards */}
      <div style={{
        position: 'relative',
        opacity: refreshing ? 0.5 : 1,
        transition: 'opacity 200ms',
        pointerEvents: refreshing ? 'none' : 'auto'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
        <KPICard
          icon={EuroIcon}
          iconColor={colors.primary.teal}
          bgColor={colors.background.lightBlue}
          borderColor={colors.primary.teal}
          label="CA Total"
          labelColor={colors.primary.gray}
          value={formatEuro(stats.caTotal)}
          valueColor={colors.primary.teal}
        />

        <KPICard
          icon={Calendar}
          iconColor={colors.primary.gold}
          bgColor={colors.background.lightYellow}
          borderColor={colors.primary.gold}
          label="Jours travaillés"
          labelColor={colors.text.brown}
          value={stats.joursTotal.toFixed(1)}
          valueColor={colors.text.brown}
        />

        <KPICard
          icon={FileClock}
          iconColor={overdueInvoices > 0 ? colors.status.error : colors.status.success}
          bgColor={overdueInvoices > 0 ? colors.background.lightRed : colors.background.lightGreen}
          borderColor={overdueInvoices > 0 ? colors.status.error : colors.status.success}
          label="Factures externe en retard"
          labelColor={overdueInvoices > 0 ? colors.text.darkRed : colors.text.darkGreen}
          value={overdueInvoices}
          valueColor={overdueInvoices > 0 ? colors.status.error : colors.text.darkGreen}
        />

        <KPICard
          icon={Send}
          iconColor={pendingInvoices > 0 ? colors.status.warning : colors.status.success}
          bgColor={pendingInvoices > 0 ? colors.background.lightYellow : colors.background.lightGreen}
          borderColor={pendingInvoices > 0 ? colors.status.warning : colors.status.success}
          label={pendingInvoices > 0 ? 'Factures externe à émettre' : 'Facturation externe à jour'}
          labelColor={pendingInvoices > 0 ? colors.text.darkOrange : colors.text.darkGreen}
          value={pendingInvoices}
          valueColor={pendingInvoices > 0 ? colors.text.goldDark : colors.text.darkGreen}
          linkTo="/facturation?statuts=Brouillon,A%20envoyer&annee="
          showExternalIcon
        />
        </div>

        {/* Charts Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem'
        }}>
        {/* Évolution du CA */}
        <ChartCard icon={TrendingUp} title="Évolution du CA">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={caParMois} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <defs>
                <linearGradient id="colorCa" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary.teal} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={colors.primary.teal} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.secondary.lightGray} />
              <XAxis
                dataKey="periode"
                tickFormatter={formatMois}
                interval={0}
                tick={{ fill: colors.secondary.mediumGray, fontSize: 12 }}
                axisLine={{ stroke: colors.secondary.lightGray }}
                tickLine={{ stroke: colors.secondary.lightGray }}
              />
              <YAxis
                tickFormatter={(value) => `${(value / 1000).toLocaleString('fr-FR')}k`}
                tick={{ fill: colors.secondary.mediumGray, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: colors.primary.gold, strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Area
                type="monotone"
                dataKey="ca"
                stroke={colors.primary.teal}
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#colorCa)"
                name="Chiffre d'affaires"
                activeDot={{ r: 6, stroke: colors.text.white, strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* CA par consultant */}
        <ChartCard icon={Users} title="CA par consultant">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={caParConsultant} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.secondary.lightGray} />
              <XAxis
                dataKey="nom"
                tick={{ fill: colors.secondary.mediumGray, fontSize: 12 }}
                axisLine={{ stroke: colors.secondary.lightGray }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                tickFormatter={(value) => `${(value / 1000).toLocaleString('fr-FR')}k`}
                tick={{ fill: colors.secondary.mediumGray, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} isAnimationActive={false} />
              <Bar dataKey="ca" fill={colors.primary.gold} name="CA généré" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Factures internes émises non payées */}
        <ChartCard
          icon={EuroIcon}
          title="Factures internes émises non payées"
          emptyMessage="Aucune facture interne émise non payée"
        >
          {facturesInternesNonPayees.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={facturesInternesNonPayees} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => formatEuro(value)}
                />
                <YAxis
                  type="category"
                  dataKey="nom"
                  width={120}
                  tickMargin={70}
                  tick={({ x, y, payload }) => {
                    const name = payload.value || '';
                    const maxChars = 12;
                    const fontSize = '0.8125rem';
                    if (name.length <= maxChars) {
                      return (
                        <text x={x} y={y} textAnchor="middle" dominantBaseline="middle" fill={colors.secondary.mediumGray} fontSize={fontSize}>
                          {name}
                        </text>
                      );
                    }
                    // Couper au dernier espace avant maxChars
                    const spaceIndex = name.lastIndexOf(' ', maxChars);
                    const line1 = spaceIndex > 0 ? name.slice(0, spaceIndex) : name.slice(0, maxChars);
                    const line2 = spaceIndex > 0 ? name.slice(spaceIndex + 1) : name.slice(maxChars);
                    return (
                      <text x={x} y={y} textAnchor="middle" fill={colors.secondary.mediumGray} fontSize={fontSize}>
                        <tspan x={x} dy={-6}>{line1}</tspan>
                        <tspan x={x} dy={12}>{line2}</tspan>
                      </text>
                    );
                  }}
                />
                <Tooltip
                  formatter={(value) => [formatEuro(value), 'Montant']}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <Bar dataKey="montant" fill={colors.primary.gold} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Jours travaillés par mois */}
        <ChartCard icon={Calendar} title="Jours travaillés par mois">
          <JoursParConsultantTable
            data={joursParConsultant.data}
            consultants={joursParConsultant.consultants}
            selectedYear={selectedYear}
          />
        </ChartCard>
        </div>
      </div>
        </AnimatedContent>
      )}
    </div>
  );
}

export default Home;
